import {
   createNoteUpdate,
   createUpdatesForMoveCopy,
   INoteRef,
   isSpecialNoteId,
   IUpdatableNotepad,
   SpecialNotes,
} from "@fokused-me/core";
import { FMBreadcrumbs, FMConfirmDialog, INoteTreeNodeMove } from "@fokused-me/web-ui";
import { List, Map } from "immutable";
import { useCallback, useMemo, useState } from "react";
import { createPanelInfo, useDesktop } from "../../../state/Desktop";
import { IEditPanelInfo, IPanelInfo } from "../../../state/Model";
import { useNotepads } from "../../../state/Notepads";

export const newNotePanel = (currentPanel: IPanelInfo, addPanel: (panel: IPanelInfo) => void) => {
   const panel = createPanelInfo({
      notepadId: currentPanel.notepadId,
      rootNoteId: currentPanel.rootNoteId,
      type: "edit",
   }) as IEditPanelInfo;
   panel.isNewNote = true;

   addPanel(panel);
};

export const useBreadcrumbs = (panelInfo: IPanelInfo, notepad?: IUpdatableNotepad, disableNavigation?: boolean) => {
   const { setPaneRootNoteId } = useDesktop();
   const { notepadInfos } = useNotepads();

   const dataHash = notepad?.getDataHash();

   const handleNavigation = useCallback(
      (itemId: string) => {
         itemId === panelInfo.notepadId
            ? setPaneRootNoteId(panelInfo.id, SpecialNotes.HOME)
            : setPaneRootNoteId(panelInfo.id, itemId);
      },
      [panelInfo, setPaneRootNoteId]
   );

   return useMemo(() => {
      let items = (
         notepad && dataHash + "1" && panelInfo.type !== "bin" ? notepad.getHierarchyIds(panelInfo.rootNoteId) : List()
      )
         .filter((id) => !isSpecialNoteId(id))
         .map((id) => ({ id, title: (notepad && notepad!.getNote(id)!.title) || "" }))
         .unshift({
            id: panelInfo.notepadId,
            title: notepadInfos.find((it) => it.id === panelInfo.notepadId)?.name || "unknown",
         });

      if (panelInfo.type === "bin") {
         items = items.push({
            id: "",
            title: "Trash Bin",
         });
      }

      if (panelInfo.type === "edit" && panelInfo.isNewNote) {
         items = items.push({
            id: "",
            title: "New note *",
         });
      }

      return <FMBreadcrumbs items={items} disableNavigation={disableNavigation} onNavigate={handleNavigation} />;
   }, [notepad, notepadInfos, dataHash, panelInfo, disableNavigation, handleNavigation]);
};

export const usePanelNodeDropped = (dstNotepadId: string, dstRootNoteId: string = SpecialNotes.HOME) => {
   const { notepads } = useNotepads();

   return useCallback(
      (noteId: string, notepadId: string) => {
         const updates = createUpdatesForMoveCopy(
            notepads,
            List<INoteRef>().push({ noteId, notepadId }),
            dstRootNoteId,
            dstNotepadId,
            0,
            false
         );
         updates.entrySeq().forEach(([npId, npUpdates]) => {
            const np = notepads.get(npId)!;
            np.apply(npUpdates);
         });
      },
      [notepads, dstNotepadId, dstRootNoteId]
   );
};

export const processNoteTreeNodeMoveEvent = (
   notepadId: string,
   notepads: Map<string, IUpdatableNotepad>,
   event: INoteTreeNodeMove,
   doCopy: boolean
) => {
   let update = false;
   const noteIds = event.items;
   let position = 0;
   let parentId = event.anchorItemId;

   const notepad = notepads.get(notepadId)!;

   if (event.anchorType === "into") {
      update = true;
   } else {
      const dstAnchorNote = notepad.getNote(event.anchorItemId);
      if (dstAnchorNote) {
         update = true;
         parentId = dstAnchorNote.parentId;

         const newPos = notepad.getNotePosition(dstAnchorNote.id, dstAnchorNote.parentId);

         if (event.anchorType === "above") {
            position = newPos;
         } else {
            position = newPos + 1;
         }
      }
   }

   if (update) {
      const updates = createUpdatesForMoveCopy(notepads, noteIds, parentId, notepadId, position, doCopy);
      updates.entrySeq().forEach(([npId, npUpdates]) => {
         const np = notepads.get(npId)!;
         np.apply(npUpdates);
      });
   }
};

export const useDeleteNoteWithConfirmation = (notepads: Map<string, IUpdatableNotepad>) => {
   const [selectedNotes, setSelectedNotes] = useState(List<INoteRef>());
   const { clearNoteSelection } = useDesktop();

   const handleDialogResult = useCallback(
      (result: number) => {
         console.log("Result: ", result);
         if (result === 0) {
            selectedNotes.forEach((ref) => {
               const update = createNoteUpdate({
                  noteId: ref.noteId,
                  parentId: SpecialNotes.BIN,
               });

               notepads.get(ref.notepadId)!.apply(List([update]));
            });

            clearNoteSelection();
         }
         setSelectedNotes(List<INoteRef>());
      },
      [notepads, selectedNotes, setSelectedNotes, clearNoteSelection]
   );

   const dialogText = useMemo(() => {
      let result = `Do you want to move ${selectedNotes.size} selected notes to Bin?`;
      if (selectedNotes.size === 1) {
         const ref = selectedNotes.get(0)!;
         const title = notepads.get(ref.notepadId)!.getNote(ref.noteId)!.title || "";
         result = `Do you want to move note "${title}" to Bin?`;
      }
      return result;
   }, [notepads, selectedNotes]);

   const confirmDeleteDialog = useMemo(
      () =>
         selectedNotes.isEmpty() ? null : (
            <FMConfirmDialog open={true} title="Move to Bin" text={dialogText} onResult={handleDialogResult} />
         ),
      [selectedNotes, dialogText, handleDialogResult]
   );

   const handleDeleteNotes = useCallback(
      (noteRefs: List<INoteRef>) => {
         setSelectedNotes(noteRefs);
      },
      [setSelectedNotes]
   );

   return {
      confirmDeleteDialog,
      handleDeleteNotes,
   };
};
